<!-- PAGE HEADER -->
<div
  class="mx-4 mb-5 w-fit rounded-2xl border border-gray-300 bg-gradient-to-r from-gray-50 to-white px-5 py-3 sm:mx-5">
  <div class="border-l-4 border-gray-800 pl-4">
    <p class="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
      Manage Application Section
    </p>

    <h2 class="mt-1 text-2xl font-bold tracking-tight text-gray-900">
      Accounts
    </h2>
  </div>
</div>

<app-manage-application-nav></app-manage-application-nav>

<!-- LOADING -->
<ng-template #loading>
  <div class="fixed inset-0 z-50 grid place-items-center bg-white/60 backdrop-blur-sm">
    <div class="grid justify-items-center gap-4 rounded-2xl border border-gray-300 bg-white px-8 py-6">
      <div class="h-10 w-10 animate-spin rounded-full border-4 border-gray-300 border-t-gray-700"></div>

      <p class="text-sm font-medium text-gray-500">
        Loading accounts...
      </p>
    </div>
  </div>
</ng-template>

<ng-container *ngIf="hasLoaded; else loading">
  <main class="mt-5 px-4 sm:px-5">
    <!-- TOOLBAR -->
    <section class="mb-5">
      <div
        class="grid grid-cols-12 items-center gap-4 rounded-2xl border border-gray-300 bg-white/95 p-4 backdrop-blur">
        <!-- ADD BUTTON -->
        <div class="col-span-12 md:col-span-3">
          <button type="button" class="primary-btn h-[50px] rounded-xl px-5 text-sm font-semibold transition"
            (click)="addNewAccount()">
            Add New Company
          </button>
        </div>

        <!-- SEARCH + TOP PAGINATOR -->
        <div class="col-span-12 md:col-span-9">
          <div class="grid grid-cols-12 items-center gap-3">
            <!-- SEARCH -->
            <div class="col-span-12 xl:col-span-7">
              <div
                class="mx-auto grid h-[50px] w-full max-w-md grid-cols-12 items-center rounded-xl border border-gray-300 bg-gray-50 px-3 transition focus-within:border-gray-300 focus-within:bg-white xl:mx-0 xl:ml-auto">
                <mat-icon class="col-span-1 flex-shrink-0 text-gray-400">
                  search
                </mat-icon>

                <input #searchInput type="text" placeholder="Search..."
                  (keyup.enter)="onSearchSubmit(searchInput.value)" (input)="searchValue = searchInput.value"
                  [(ngModel)]="searchValue"
                  class="col-span-10 min-w-0 bg-transparent px-2 text-sm text-gray-700 outline-none placeholder-gray-400" />

                <button type="button"
                  class="col-span-1 grid place-items-center rounded-full p-1.5 text-gray-500 transition hover:bg-gray-100 hover:text-gray-700"
                  [class.opacity-0]="!searchValue" [class.pointer-events-none]="!searchValue"
                  (click)="clearSearch(); searchInput.value=''; searchValue=''">
                  <mat-icon class="cursor-pointer text-lg">
                    close
                  </mat-icon>
                </button>
              </div>
            </div>

            <!-- TOP PAGINATOR -->
            <div class="col-span-12 grid justify-items-center xl:col-span-5 xl:justify-items-end">
              <div class="w-fit overflow-hidden rounded-xl bg-white">
                <mat-paginator [length]="totalItems" [pageSize]="pageSize" [pageIndex]="currentPage - 1"
                  [pageSizeOptions]="[pageSize]" showFirstLastButtons (page)="onPageChange($event)">
                </mat-paginator>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ACCOUNTS TABLE -->
    <section class="grid grid-cols-1 gap-4">
      <div class="col-span-1 overflow-hidden rounded-2xl border border-gray-300 bg-white">
        <div class="w-full overflow-x-auto">
          <mat-table [dataSource]="dataSource" matSort
            class="!inline-table !w-full min-w-[1190px] table-fixed table-alternate text-sm"
            (matSortChange)="onSortChange($event)">
            <!-- ID COLUMN -->
            <ng-container matColumnDef="ID">
              <th mat-header-cell *matHeaderCellDef mat-sort-header
                class="w-[110px] whitespace-nowrap bg-gray-50 px-5 py-4 text-left text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">
                ID
              </th>

              <td mat-cell *matCellDef="let element"
                class="w-[110px] whitespace-nowrap px-5 py-5 text-sm font-medium text-gray-800">
                {{ element.ID }}
              </td>
            </ng-container>

            <!-- ACTIVE FROM COLUMN -->
            <ng-container matColumnDef="created_at">
              <th mat-header-cell *matHeaderCellDef mat-sort-header
                class="w-[120px] whitespace-nowrap bg-gray-50 px-5 py-4 text-left text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">
                Active From
              </th>

              <td mat-cell *matCellDef="let element"
                class="w-[120px] whitespace-nowrap px-5 py-5 text-sm text-gray-600">
                {{ element.created_at | date:'dd/MM/yyyy' }}
              </td>
            </ng-container>

            <!-- PRICING PLAN COLUMN -->
            <ng-container matColumnDef="pricing_plan">
              <th mat-header-cell *matHeaderCellDef mat-sort-header
                class="w-[150px] whitespace-nowrap bg-gray-50 px-5 py-4 text-left text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">
                Pricing Plan
              </th>

              <td mat-cell *matCellDef="let element"
                class="w-[150px] whitespace-nowrap px-5 py-5 text-sm font-medium text-gray-800">
                {{ element.pricing_plan || '---' }}
              </td>
            </ng-container>

            <!-- NAME COLUMN -->
            <ng-container matColumnDef="Name">
              <th mat-header-cell *matHeaderCellDef mat-sort-header
                class="bg-gray-50 px-5 py-4 text-left text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">
                Company Name
              </th>

              <td mat-cell *matCellDef="let element" class="px-5 py-5 text-sm font-medium text-gray-800">
                {{ element.Name }}
              </td>
            </ng-container>

            <!-- ACTIONS COLUMN -->
            <ng-container matColumnDef="actions">
              <th mat-header-cell *matHeaderCellDef
                class="actions-center w-[220px] whitespace-nowrap bg-gray-50 px-5 py-4 text-center text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">
                Actions
              </th>

              <td mat-cell *matCellDef="let element" class="actions-center w-[220px] whitespace-nowrap px-5 py-5">
                <div class="actions-wrap grid w-full grid-cols-3 items-center justify-center gap-1">
                  <button mat-icon-button type="button" matTooltip="Preview Index" matTooltipPosition="above"
                    class="transition-colors hover:text-blue-600" (click)="previewIndex(element)">
                    <mat-icon>visibility</mat-icon>
                  </button>

                  <button mat-icon-button type="button" matTooltip="Edit" matTooltipPosition="above"
                    class="transition-colors hover:text-blue-600" (click)="editAccount(element)">
                    <mat-icon>edit</mat-icon>
                  </button>

                  <button mat-icon-button type="button" matTooltip="Delete" matTooltipPosition="above"
                    class="transition-colors hover:text-red-600" (click)="openDeleteConfirm(element)">
                    <mat-icon>delete</mat-icon>
                  </button>
                </div>
              </td>
            </ng-container>

            <!-- HEADER AND ROW DEFINITIONS -->
            <tr mat-header-row *matHeaderRowDef="displayedColumns" class="border-b border-gray-300 bg-gray-50"></tr>

            <tr mat-row *matRowDef="let row; columns: displayedColumns;"
              class="border-b border-gray-100 bg-white transition-colors even:bg-gray-50/40 hover:bg-gray-100"></tr>
          </mat-table>
        </div>
      </div>

      <!-- BOTTOM PAGINATOR -->
      <div *ngIf="dataSource.data.length > 0"
        class="col-span-1 grid grid-cols-12 items-center rounded-2xl bg-white px-3 py-2">
        <div class="col-span-12 grid justify-items-end">
          <div class="w-fit overflow-hidden rounded-xl bg-white">
            <mat-paginator [length]="totalItems" [pageSize]="pageSize" [pageIndex]="currentPage - 1"
              [pageSizeOptions]="[pageSize]" showFirstLastButtons (page)="onPageChange($event)">
            </mat-paginator>
          </div>
        </div>
      </div>
    </section>
  </main>
</ng-container>

<!-- TEMPLATES -->
<ng-template #companyFormTmpl>
  <app-company-form [formData]="selectedCompany" (onSubmitClbk)="onSubmitCompanyClbk()">
  </app-company-form>
</ng-template>

<ng-template #confirmDeleteTmpl>
  <div class="grid gap-4 p-4">
    <p>
      Are you sure you want to delete company
      <b>{{ selectedCompany.Name }}</b>?
    </p>

    <div class="grid grid-cols-2 items-center justify-center gap-2">
      <button type="button" class="base-btn rounded bg-red-600 px-4 py-2 text-white" (click)="deleteAccount()">
        Confirm
      </button>

      <button type="button" class="base-btn rounded bg-gray-300 px-4 py-2 text-gray-800" (click)="dialog.closeAll()">
        Cancel
      </button>
    </div>
  </div>
</ng-template>